import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, Table, Button, Modal, Form, Select, DatePicker, Input, Space, message, Popconfirm, Row, Col, Upload, Calendar, Tooltip } from "antd";
import { PlusOutlined, UploadOutlined, DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import PageTransition from "../../components/ui/PageTransition";
import PageHeader from "../../components/ui/PageHeader";
import StatusTag from "../../components/ui/StatusTag";
import EmptyState from "../../components/ui/EmptyState";
import ViewToggle from "../../components/ui/ViewToggle";
import { LeaveCard, CalendarHeader, STATUS } from "../Attendance/MyMonthTab";

const { RangePicker } = DatePicker;

const LEAVE_STATUS_COLORS = { pending: "#F59E0B", approved: "#14B8A6", rejected: "#EF4444" };

const Leave = () => {
  const { request } = useRequest();
  const [requests, setRequests] = useState([]);
  const [balances, setBalances] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [documentFile, setDocumentFile] = useState(null);
  const [view, setView] = useState("list");
  const [submitting, setSubmitting] = useState(false);
  const [yearly, setYearly] = useState(null); // annual / sick yearly balance
  const [calValue, setCalValue] = useState(moment()); // month shown on the calendar
  const [dayStatus, setDayStatus] = useState({}); // date -> attendance status for that month

  const [form] = Form.useForm();
  // Past days can't be requested, except sick leave which can be back-dated by up to 2 days.
  const selectedType = Form.useWatch("leaveType", form);
  // Annual and sick leave have a yearly allowance: the dropdown shows what is still available
  // (left, minus days already waiting for approval); other types keep their own balance.
  const kpiFor = (name) => (yearly ? (/sick/i.test(name) ? yearly.sick : /annual/i.test(name) ? yearly.annual : null) : null);
  const availableOf = (kpi) => Math.max(0, kpi.remaining - kpi.pending);
  const fmtDays = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(1));
  const leaveTypeOptions = leaveTypes.map((t) => {
    const kpi = kpiFor(t.name);
    return { ...t, label: `${t.name} (${kpi ? fmtDays(availableOf(kpi)) : t.balance} available)` };
  });
  const selectedName = leaveTypes.find((t) => t.value === selectedType)?.name || "";
  const selectedKpi = kpiFor(selectedName);
  const selectedRange = Form.useWatch("range", form);
  const requestedDays = selectedRange && selectedRange[0] && selectedRange[1] ? selectedRange[1].diff(selectedRange[0], "days") + 1 : 0;
  const limitMessage = (days) => {
    if (!selectedKpi) return null;
    const label = /sick/i.test(selectedName) ? "sick leave" : "annual leave";
    const available = availableOf(selectedKpi);
    if (available <= 0) return `You have no ${label} remaining for ${selectedKpi.entitled ? yearly.year : "this year"}.`;
    if (days > available) return `You only have ${fmtDays(available)} ${label} day${available === 1 ? "" : "s"} remaining. Please reduce the dates.`;
    return null;
  };
  const isSickSelected = /sick/i.test(leaveTypes.find((t) => t.value === selectedType)?.name || "");
  const earliestDay = moment().startOf("day").subtract(isSickSelected ? 2 : 0, "days");

  const fetchMine = () => {
    request({
      url: apiPath.leaveRequestsMine,
      method: "GET",
      onSuccess: (res) => {
        setRequests(res?.data?.requests || []);
        setBalances(res?.data?.balances || []);
      },
    });
  };

  // Attendance status of each day of the month shown (weekend, holiday, present ...)
  useEffect(() => {
    if (view !== "calendar") return;
    request({
      url: `${apiPath.attendanceMonth}?year=${calValue.year()}&month=${calValue.month() + 1}`,
      method: "GET",
      onSuccess: (res) => {
        const map = {};
        (res?.data?.days || []).forEach((d) => { map[d.date] = d; });
        setDayStatus(map);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, calValue.year(), calValue.month()]);

  useEffect(() => {
    request({ url: apiPath.attendanceMonth, method: "GET", onSuccess: (res) => setYearly(res?.data?.leaveBalance || null) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchMine();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    form.resetFields();
    setDocumentFile(null);
    setSubmitting(false);
    setLeaveTypes(balances.map((b) => ({ value: b.leaveType, name: b.leaveTypeName, balance: b.balance })));
    setModalOpen(true);
  };

  const onSubmit = () => {
    // Without this guard, a slow request + no visible feedback on the OK
    // button led to users clicking it repeatedly, creating duplicates.
    if (submitting) return;
    form.validateFields().then((values) => {
      const [start, end] = values.range;
      const blocked = limitMessage(end.diff(start, "days") + 1);
      if (blocked) {
        message.error(blocked);
        return;
      }
      const formData = new FormData();
      formData.append("leaveType", values.leaveType);
      formData.append("startDate", start.format("YYYY-MM-DD"));
      formData.append("endDate", end.format("YYYY-MM-DD"));
      formData.append("reason", values.reason || "");
      if (documentFile) formData.append("file", documentFile);

      setSubmitting(true);
      request({
        url: apiPath.leaveRequests,
        method: "POST",
        data: formData,
        header: { "Content-Type": "multipart/form-data" },
        onSuccess: () => {
          setSubmitting(false);
          message.success("Leave request submitted");
          setModalOpen(false);
          fetchMine();
        },
        onError: (err) => {
          setSubmitting(false);
          message.error(err?.response?.data?.message || "Could not submit leave request");
        },
      });
    });
  };

  const onDownloadDocument = async (record) => {
    try {
      const token = localStorage.getItem("i_ems_employee_token") || "";
      const response = await axios.get(`${apiPath.baseURL}${apiPath.leaveRequestDocumentDownload(record._id)}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", record.supportingDocumentFileName || "document");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      message.error("Could not download document");
    }
  };

  const onCancel = (record) => {
    request({
      url: `${apiPath.leaveRequests}/${record._id}/cancel`,
      method: "PUT",
      onSuccess: () => {
        message.success("Leave request cancelled");
        fetchMine();
      },
    });
  };

  const columns = [
    { title: "Leave Type", dataIndex: ["leaveType", "name"] },
    { title: "From", dataIndex: "startDate" },
    { title: "To", dataIndex: "endDate" },
    { title: "Days", dataIndex: "days" },
    { title: "Reason", dataIndex: "reason", ellipsis: true },
    { title: "Status", dataIndex: "status", render: (s) => <StatusTag status={s} /> },
    { title: "Comment", dataIndex: "adminComment" },
    { title: "Clarification", dataIndex: "clarificationNote" },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          {record.supportingDocumentKey && (
            <Button size="small" icon={<DownloadOutlined />} onClick={() => onDownloadDocument(record)}>
              Document
            </Button>
          )}
          {record.status === "pending" && (
            <Popconfirm title="Cancel this leave request?" onConfirm={() => onCancel(record)}>
              <Button size="small" danger>
                Cancel
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // Leave calendar: every day within [startDate, endDate] of one of the
  // employee's own requests gets a colored tag (color-by-status mirrors
  // ems-admin's LeaveRequests calendar, minus the employee-name prefix
  // since it's all the current employee's own data here).
  const leaveDateCellRender = (value) => {
    const dateStr = value.format("YYYY-MM-DD");
    const matches = requests.filter((r) => r.startDate <= dateStr && r.endDate >= dateStr);
    const isPast = !value.isSameOrAfter(moment(), "day");

    const day = dayStatus[dateStr];
    const st = day && day.status !== "leave" && day.status !== "none" ? STATUS[day.status] : null;

    return (
      <div className="i-ems-calendar-cell-content">
        {st && value.month() === calValue.month() ? (
          <div style={{ color: st.color, background: st.bg, borderLeft: `3px solid ${st.color}`, borderRadius: 4, padding: "0 6px", fontSize: 11, fontWeight: 600 }}>
            {st.label}
          </div>
        ) : null}
        {matches.map((r) => {
          const label = r.leaveType?.name || "Leave";
          const color = LEAVE_STATUS_COLORS[r.status] || "#9CA3AF";
          return (
            <Tooltip key={r._id} title={`${label} — ${r.status}`}>
              <div className="i-ems-calendar-tag" style={{ color, background: `${color}1A` }}>
                {label}
              </div>
            </Tooltip>
          );
        })}
        {!isPast && (
          <div className="i-ems-calendar-apply-wrap">
            <Button
              size="small"
              type="primary"
              className="i-ems-calendar-apply-btn"
              icon={<PlusOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                openCreate();
                form.setFieldsValue({ range: [value, value] });
              }}
            >
              Apply
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <PageTransition>
      <PageHeader
        title="Leave"
        subtitle="Manage your leave requests and balances"
        actions={<ViewToggle value={view} onChange={setView} />}
      />

      {yearly ? (
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={12}><LeaveCard title="Annual leave" color="#8B5CF6" kpi={yearly.annual} year={yearly.year} /></Col>
          <Col xs={24} md={12}><LeaveCard title="Sick leave" color="#F59E0B" kpi={yearly.sick} year={yearly.year} /></Col>
        </Row>
      ) : null}

      {view === "list" ? (
        <>
          <Space style={{ marginBottom: 16, width: "100%", justifyContent: "flex-end" }}>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Apply for Leave
            </Button>
          </Space>
          <Card>
            <div style={{ overflowX: "auto" }}>
              <Table
                rowKey="_id"
                columns={columns}
                dataSource={requests}
                pagination={{ pageSize: 10 }}
                locale={{ emptyText: <EmptyState message="No leave requests yet" actionLabel="Apply for Leave" onAction={openCreate} /> }}
              />
            </div>
          </Card>
        </>
      ) : (
        <Card>
          <div className="i-ems-apply-calendar">
            <Calendar
              value={calValue}
              onPanelChange={(v) => setCalValue(v)}
              onChange={(v) => { if (v.month() !== calValue.month() || v.year() !== calValue.year()) setCalValue(v); }}
              dateCellRender={leaveDateCellRender}
              headerRender={({ value: v, onChange }) => <CalendarHeader value={v} onChange={onChange} />}
            />
          </div>
        </Card>
      )}

      <Modal
        title="Apply for Leave"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={onSubmit}
        confirmLoading={submitting}
        okButtonProps={{ disabled: submitting }}
        cancelButtonProps={{ disabled: submitting }}
        destroyOnClose
      >
        <Form layout="vertical" form={form}>
          <Form.Item name="leaveType" label="Leave Type" rules={[{ required: true }]}>
            <Select options={leaveTypeOptions} placeholder="Select leave type" />
          </Form.Item>
          <Form.Item name="range" label="Date Range" rules={[{ required: true }]} extra={
              <>
                {selectedKpi ? <div>{`${fmtDays(availableOf(selectedKpi))} ${isSickSelected ? "sick" : "annual"} leave day(s) available this year.`}</div> : null}
                {isSickSelected ? <div>Sick leave can be back-dated by up to 2 days.</div> : null}
                {limitMessage(requestedDays) && requestedDays > 0 ? <div style={{ color: "#D64545" }}>{limitMessage(requestedDays)}</div> : null}
              </>
            }>
            <RangePicker style={{ width: "100%" }} disabledDate={(current) => current && current < earliestDay} />
          </Form.Item>
          <Form.Item name="reason" label="Reason">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item label="Supporting Document (PDF/JPEG/PNG, optional)">
            <Upload
              beforeUpload={(file) => {
                setDocumentFile(file);
                return false;
              }}
              onRemove={() => setDocumentFile(null)}
              maxCount={1}
              accept=".pdf,.jpg,.jpeg,.png"
            >
              <Button icon={<UploadOutlined />}>Select File</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </PageTransition>
  );
};

export default Leave;
